import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import Handler from "../../../Helpers/Handler";
import { textCapitalize } from "../../../Helpers/Utils";
import { Skeleton } from "../../../Components/Needed";

// Security-deposit amount per vehicle segment/sub-segment - the same
// segment/subSegment pair Car -> VS -> Model resolves to when pricing a
// booking (see BookingUtils.resolveCar / PricingUtils.bandedAmount).
const SEGMENTS = [
    { key: "hatchback", label: "Hatchback", subs: ["small", "compact", "premium", "luxury"] },
    { key: "sedan", label: "Sedan", subs: ["compact", "premium", "luxury"] },
    { key: "suv", label: "SUV", subs: ["compact", "mpv", "xuv", "luxury"] },
];

const buildEmptyForm = () => {
    const form = {};
    SEGMENTS.forEach((segment) => {
        form[segment.key] = {};
        segment.subs.forEach((sub) => { form[segment.key][sub] = "0"; });
    });
    return form;
};

const SegmentTab = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState(buildEmptyForm);

    const load = useCallback(async () => {
        setLoading(true);
        const response = await Handler({ method: "GET", url: "/staff/business/site/segment/view" });
        if (response?.success) {
            const data = response?.data?.data || {};
            const next = buildEmptyForm();
            SEGMENTS.forEach((segment) => {
                segment.subs.forEach((sub) => {
                    const value = data?.[segment.key]?.[sub];
                    if (value !== undefined) next[segment.key][sub] = String(value);
                });
            });
            setForm(next);
        }
        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    const handleChange = (segmentKey, subKey, value) => {
        setForm((prev) => ({ ...prev, [segmentKey]: { ...prev[segmentKey], [subKey]: value } }));
    };

    const onSave = async () => {
        const payload = {};
        for (const segment of SEGMENTS) {
            payload[segment.key] = {};
            for (const sub of segment.subs) {
                const value = Number(form[segment.key][sub]);
                if (Number.isNaN(value) || value < 0) {
                    toast.error(`${segment.label} - ${sub} must be a non-negative number`);
                    return;
                }
                payload[segment.key][sub] = value;
            }
        }
        setSaving(true);
        const response = await Handler({ method: "patch", url: "/staff/business/site/segment", data: payload });
        if (response?.success) {
            toast.success(response?.data?.message || "Segment pricing updated");
            load();
        } else {
            toast.error(textCapitalize(response?.data?.message || "Unable to update segment pricing"));
        }
        setSaving(false);
    };

    if (loading) {
        return (
            <div className="settings-card">
                <Skeleton height={24} width="40%" className="mb-3" />
                <Skeleton height={120} className="mb-3" />
            </div>
        );
    }

    return (
        <div className="settings-card">
            <h5 className="fw-semibold mb-3">Segment Security Deposit</h5>
            <p className="text-muted small mb-4">The refundable security deposit charged per vehicle segment and sub-segment.</p>

            {SEGMENTS.map((segment) => (
                <div className="segment-group" key={segment.key}>
                    <div className="segment-group-title">{segment.label}</div>
                    <div className="row g-3">
                        {segment.subs.map((sub) => (
                            <div className="col-6 col-md-3" key={sub}>
                                <label className="form-label text-capitalize small" htmlFor={`segment-${segment.key}-${sub}`}>{sub}</label>
                                <div className="input-group input-group-sm">
                                    <span className="input-group-text">₹</span>
                                    <input
                                        id={`segment-${segment.key}-${sub}`}
                                        type="number"
                                        min="0"
                                        className="form-control"
                                        value={form[segment.key][sub]}
                                        onChange={(e) => handleChange(segment.key, sub, e.target.value)}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ))}

            <div className="settings-card-actions">
                <button type="button" className="btn btn-primary" onClick={onSave} disabled={saving}>
                    {saving ? "Saving..." : "Save changes"}
                </button>
            </div>
        </div>
    );
};

export default SegmentTab;
